import { useState, useEffect } from 'react';
import {
    Dialog, DialogTitle, DialogContent, DialogActions,
    Button, IconButton, Box, Tabs, Tab, Typography,
    CircularProgress, Chip, Checkbox, List, ListItem,
    ListItemButton, ListItemText, ListItemIcon,
} from '@mui/material';
import { FaTimes, FaSyncAlt } from 'react-icons/fa';
import { toast } from 'react-toastify';
import api from '../../services/api';
import {
    PendentesSincronizacao, SincronizarRequest, ResultadoSincronizacao,
    TipoPendencia, LocatarioPendente, VeiculoPendente, VinculoPendente, AcessoAppPendente,
} from '../../services/tipos';

export type TipoAbaSincronizacao = 'locatarios' | 'veiculos' | 'vinculos' | 'acessos';

interface ModalSincronizacaoProps {
    open: boolean;
    onClose: () => void;
    /**
     * Restringe quais abas/categorias aparecem e podem ser sincronizadas.
     * Default = todas (motoristas, veículos, vínculos, acessos app).
     * Útil quando o modal é aberto a partir de uma tela específica (ex.: listagem de locatários).
     */
    abasVisiveis?: TipoAbaSincronizacao[];
    titulo?: string;
}

const ABAS_ORDEM: TipoAbaSincronizacao[] = ['locatarios', 'veiculos', 'vinculos', 'acessos'];

export default function ModalSincronizacao({ open, onClose, abasVisiveis, titulo }: ModalSincronizacaoProps) {
    const abasAtivas: TipoAbaSincronizacao[] = abasVisiveis && abasVisiveis.length > 0
        ? ABAS_ORDEM.filter(a => abasVisiveis.includes(a))
        : ABAS_ORDEM;

    const mostrarLocatarios = abasAtivas.includes('locatarios');
    const mostrarVeiculos = abasAtivas.includes('veiculos');
    const mostrarVinculos = abasAtivas.includes('vinculos');
    const mostrarAcessos = abasAtivas.includes('acessos');

    const [carregando, setCarregando] = useState(false);
    const [importando, setImportando] = useState(false);
    const [pendentes, setPendentes] = useState<PendentesSincronizacao | null>(null);
    const [abaAtiva, setAbaAtiva] = useState(0);

    const [locatariosSelecionados, setLocatariosSelecionados] = useState<Set<string>>(new Set());
    const [veiculosSelecionados, setVeiculosSelecionados] = useState<Set<string>>(new Set());
    const [vinculosSelecionados, setVinculosSelecionados] = useState<Set<string>>(new Set());
    const [acessosSelecionados, setAcessosSelecionados] = useState<Set<string>>(new Set());

    const carregarPendentes = async () => {
        setCarregando(true);
        try {
            const response = await api.get<PendentesSincronizacao>('/LocaSmartCheck/Pendentes');
            setPendentes(response.data);
            // Pré-selecionar todos os Faltando das abas visíveis, deixar Divergente desselecionado.
            // Categorias fora de abasVisiveis ficam vazias pra não serem enviadas inadvertidamente.
            setLocatariosSelecionados(mostrarLocatarios
                ? new Set(response.data.locatarios.filter(l => l.tipo === TipoPendencia.Faltando).map(l => l.id))
                : new Set());
            setVeiculosSelecionados(mostrarVeiculos
                ? new Set(response.data.veiculos.filter(v => v.tipo === TipoPendencia.Faltando).map(v => v.id))
                : new Set());
            setVinculosSelecionados(mostrarVinculos
                ? new Set(response.data.vinculos.map(v => `${v.veiculoId}|${v.locatarioId}`))
                : new Set());
            setAcessosSelecionados(mostrarAcessos
                ? new Set(response.data.acessosApp.map(a => a.locatarioId))
                : new Set());
        } catch (error) {
            toast.error('Erro ao carregar pendentes do LocaSmartCheck');
        } finally {
            setCarregando(false);
        }
    };

    useEffect(() => {
        if (open) {
            carregarPendentes();
        }
    }, [open]);

    const toggleSet = (set: Set<string>, key: string): Set<string> => {
        const nova = new Set(set);
        if (nova.has(key)) nova.delete(key); else nova.add(key);
        return nova;
    };

    const handleImportar = async () => {
        if (!pendentes) return;
        setImportando(true);
        try {
            const payload: SincronizarRequest = {
                locatariosIds: Array.from(locatariosSelecionados),
                veiculosIds: Array.from(veiculosSelecionados),
                vinculos: Array.from(vinculosSelecionados).map(s => {
                    const [veiculoId, locatarioId] = s.split('|');
                    return { veiculoId, locatarioId };
                }),
                acessosAppLocatarios: Array.from(acessosSelecionados),
            };
            const response = await api.post<ResultadoSincronizacao>('/LocaSmartCheck/Sincronizar', payload);
            const r = response.data;
            const total = r.locatariosSincronizados + r.veiculosSincronizados + r.vinculosSincronizados + r.acessosAppSincronizados;
            if (r.erros.length > 0) {
                toast.warning(`${total} item(s) sincronizado(s), ${r.erros.length} erro(s).`);
            } else {
                toast.success(`${total} item(s) sincronizado(s) com sucesso!`);
            }
            await carregarPendentes();
        } catch (error) {
            toast.error('Erro ao sincronizar com LocaSmartCheck');
        } finally {
            setImportando(false);
        }
    };

    const totalSelecionados = locatariosSelecionados.size + veiculosSelecionados.size + vinculosSelecionados.size + acessosSelecionados.size;

    if (!open) return null;

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span>{titulo ?? 'Sincronizar com LocaSmartCheck'}</span>
                <IconButton onClick={onClose}><FaTimes /></IconButton>
            </DialogTitle>
            <DialogContent dividers>
                {carregando ? (
                    <Box sx={{ textAlign: 'center', py: 4 }}>
                        <CircularProgress />
                        <Typography sx={{ mt: 2 }}>Verificando o que está fora de sincronia...</Typography>
                    </Box>
                ) : !pendentes?.empresaIntegrada ? (
                    <Typography color="error">A empresa não está integrada ao LocaSmartCheck. Ative a integração primeiro.</Typography>
                ) : (
                    <>
                        {abasAtivas.length > 1 && (
                            <Tabs value={abaAtiva} onChange={(_, v) => setAbaAtiva(v)} sx={{ borderBottom: 1, borderColor: 'divider' }}>
                                {mostrarLocatarios && <Tab label={`Motoristas (${pendentes.locatarios.length})`} />}
                                {mostrarVeiculos && <Tab label={`Veículos (${pendentes.veiculos.length})`} />}
                                {mostrarVinculos && <Tab label={`Vínculos (${pendentes.vinculos.length})`} />}
                                {mostrarAcessos && <Tab label={`Acesso App (${pendentes.acessosApp.length})`} />}
                            </Tabs>
                        )}
                        <Box sx={{ pt: 2 }}>
                            {abasAtivas[abaAtiva] === 'locatarios' && (
                                <ListaLocatarios items={pendentes.locatarios} selecionados={locatariosSelecionados}
                                    onToggle={id => setLocatariosSelecionados(toggleSet(locatariosSelecionados, id))} />
                            )}
                            {abasAtivas[abaAtiva] === 'veiculos' && (
                                <ListaVeiculos items={pendentes.veiculos} selecionados={veiculosSelecionados}
                                    onToggle={id => setVeiculosSelecionados(toggleSet(veiculosSelecionados, id))} />
                            )}
                            {abasAtivas[abaAtiva] === 'vinculos' && (
                                <ListaVinculos items={pendentes.vinculos} selecionados={vinculosSelecionados}
                                    onToggle={key => setVinculosSelecionados(toggleSet(vinculosSelecionados, key))} />
                            )}
                            {abasAtivas[abaAtiva] === 'acessos' && (
                                <ListaAcessos items={pendentes.acessosApp} selecionados={acessosSelecionados}
                                    onToggle={id => setAcessosSelecionados(toggleSet(acessosSelecionados, id))} />
                            )}
                        </Box>
                    </>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={carregarPendentes} disabled={carregando || importando} startIcon={<FaSyncAlt />}>
                    Atualizar
                </Button>
                <Button variant="contained" onClick={handleImportar}
                    disabled={carregando || importando || totalSelecionados === 0 || !pendentes?.empresaIntegrada}>
                    {importando ? 'Importando...' : `Importar ${totalSelecionados} selecionado(s)`}
                </Button>
            </DialogActions>
        </Dialog>
    );
}

function ListaLocatarios({ items, selecionados, onToggle }: { items: LocatarioPendente[]; selecionados: Set<string>; onToggle: (id: string) => void }) {
    if (items.length === 0) return <Typography color="text.secondary">Nenhum motorista pendente.</Typography>;
    return (
        <List>
            {items.map(l => (
                <ListItem key={l.id} disablePadding>
                    <ListItemButton onClick={() => onToggle(l.id)}>
                        <ListItemIcon><Checkbox edge="start" checked={selecionados.has(l.id)} tabIndex={-1} /></ListItemIcon>
                        <ListItemText
                            primary={`${l.nome} — CPF ${l.documento}`}
                            secondary={
                                <Chip
                                    size="small"
                                    label={l.tipo === TipoPendencia.Faltando ? 'Faltando' : `Divergente: ${l.camposDivergentes.join(', ')}`}
                                    color={l.tipo === TipoPendencia.Faltando ? 'error' : 'warning'}
                                />
                            }
                        />
                    </ListItemButton>
                </ListItem>
            ))}
        </List>
    );
}

function ListaVeiculos({ items, selecionados, onToggle }: { items: VeiculoPendente[]; selecionados: Set<string>; onToggle: (id: string) => void }) {
    if (items.length === 0) return <Typography color="text.secondary">Nenhum veículo pendente.</Typography>;
    return (
        <List>
            {items.map(v => (
                <ListItem key={v.id} disablePadding>
                    <ListItemButton onClick={() => onToggle(v.id)}>
                        <ListItemIcon><Checkbox edge="start" checked={selecionados.has(v.id)} tabIndex={-1} /></ListItemIcon>
                        <ListItemText
                            primary={`${v.placa} — ${v.marcaModelo}`}
                            secondary={
                                <Chip
                                    size="small"
                                    label={v.tipo === TipoPendencia.Faltando ? 'Faltando' : `Divergente: ${v.camposDivergentes.join(', ')}`}
                                    color={v.tipo === TipoPendencia.Faltando ? 'error' : 'warning'}
                                />
                            }
                        />
                    </ListItemButton>
                </ListItem>
            ))}
        </List>
    );
}

function ListaVinculos({ items, selecionados, onToggle }: { items: VinculoPendente[]; selecionados: Set<string>; onToggle: (key: string) => void }) {
    if (items.length === 0) return <Typography color="text.secondary">Nenhum vínculo pendente.</Typography>;
    return (
        <List>
            {items.map(v => {
                const key = `${v.veiculoId}|${v.locatarioId}`;
                return (
                    <ListItem key={key} disablePadding>
                        <ListItemButton onClick={() => onToggle(key)}>
                            <ListItemIcon><Checkbox edge="start" checked={selecionados.has(key)} tabIndex={-1} /></ListItemIcon>
                            <ListItemText
                                primary={`${v.locatarioNome} → ${v.veiculoPlaca}`}
                                secondary={<Chip size="small" label="Faltando" color="error" />}
                            />
                        </ListItemButton>
                    </ListItem>
                );
            })}
        </List>
    );
}

function ListaAcessos({ items, selecionados, onToggle }: { items: AcessoAppPendente[]; selecionados: Set<string>; onToggle: (id: string) => void }) {
    if (items.length === 0) return <Typography color="text.secondary">Nenhum acesso ao app pendente.</Typography>;
    return (
        <List>
            {items.map(a => (
                <ListItem key={a.locatarioId} disablePadding>
                    <ListItemButton onClick={() => onToggle(a.locatarioId)}>
                        <ListItemIcon><Checkbox edge="start" checked={selecionados.has(a.locatarioId)} tabIndex={-1} /></ListItemIcon>
                        <ListItemText
                            primary={`${a.locatarioNome} — ${a.email}`}
                            secondary={<Chip size="small" label="Habilitar acesso" color="error" />}
                        />
                    </ListItemButton>
                </ListItem>
            ))}
        </List>
    );
}
